<script lang="ts">
export default {
  name: "WishlistPage",
};
</script>

<script setup lang="ts">
import type { Schemas } from "#shopware";
import SwProductCard from "~/components/cms/SwProductCard.vue";

const { items, clearWishlist } = useWishlist();
const { apiClient } = useShopwareContext();
const products = ref<Schemas["Product"][]>([]);
const isLoading = ref(false);
const { t } = useI18n();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
useBreadcrumbs([
  {
    name: t("breadcrumbs.wishlist"),
    path: "/wishlist",
  },
]);
const clearWishlistHandler = async () => {
  try {
    isLoading.value = true;
    await clearWishlist();
  } finally {
    isLoading.value = false;
  }
};
const loadProductsByItemIds = async (itemIds: string[]): Promise<void> => {
  isLoading.value = true;

  try {
    const result = await apiClient.invoke("readProduct post /product", {
      body: {
        ids: itemIds || items.value,
        associations: {"seoUrls": []},
      }
    });

    if (result?.data.elements) {
      products.value = result.data.elements;
    }
  } catch (error) {
    console.error("[wishlist][loadProductsByItemIds]", error);
  }

  isLoading.value = false;
};

watch(
  items,
  (items, oldItems) => {
    if (items.length !== oldItems?.length) {
      products.value = products.value.filter(({ id }) => items?.includes(id));
    }
    if (!items.length) {
      return;
    }
    loadProductsByItemIds(items);
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <LayoutBreadcrumbs />
  <div class="container wishlist-page" data-testid="wishlist-wrapper">
    <div class="max-w-2xl mx-auto py-4 px-4 sm:py-4 sm:px-6 lg:max-w-7xl lg:px-8">
      <!-- Wishlist is completed -->
      <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center z-10 bg-white/50">
        <div class="h-15 w-15 i-carbon-progress-bar-round animate-spin c-secondary-500"/>
      </div>

      <div v-else-if="products.length" class="row">
        <div class="col-12 col-md-6 mb-5">
          <h1 class="my-3 text-3xl font-extrabold">
            {{ $t("wishlist.header") }}
          </h1>
        </div>

        <div class="col-12 col-md-6 text-end">
          <button
              class="btn btn-primary"
              data-testid="clear-wishlist-button"
              @click="clearWishlistHandler"
          >
            {{ $t("wishlist.clearWishlist") }}
          </button>
        </div>

        <div class="row">
          <SwProductCard
            v-for="product in products"
            :key="product.id"
            :product="product"
            :is-product-listing="true"
            class="pb-4"
          />
        </div>
      </div>
      <!-- Empty of wishlist -->
      <div
        v-else
        class="flex flex-col items-center col mx-auto"
        data-testid="wishlist-empty"
      >
        <div class="w-48 h-48 i-carbon-favorite" />
        <h1 class="my-3 text-3xl font-extrabold">
          {{ $t("wishlist.emptyLabel") }}
        </h1>
        <p class="my-4">{{ $t("wishlist.emptyText") }}</p>
        <NuxtLink
          :to="formatLink(`/`)"
          class="btn btn-primary"
          date-testid="wishlist-empty-continue-link"
        >
          {{ $t("wishlist.continueShopping") }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
